import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '../api';

function Profile({ setIsAuthenticated }) {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await usersAPI.getMe();
      setUser(response.data);
      setEmail(response.data.email || '');
    } catch (error) {
      console.error('Failed to load profile:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    navigate('/');
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await usersAPI.updateEmail(email);
      setMessage('Email updated successfully!');
      
      // Update local storage
      const userData = JSON.parse(localStorage.getItem('user'));
      userData.email = email;
      localStorage.setItem('user', JSON.stringify(userData));
      
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await usersAPI.updatePassword(currentPassword, newPassword);
      setMessage('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="loading">Loading profile...</div>;
  }

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>Profile Settings</h1>
            <p style={{ color: '#666', marginTop: '5px' }}>Manage your account</p>
          </div>
          <div className="user-info">
            <button 
              className="btn" 
              onClick={() => navigate('/dashboard')}
              style={{ marginRight: '10px', background: '#667eea' }}
            >
              Back to Dashboard
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          {message && (
            <div style={{
              background: '#d4edda',
              color: '#155724',
              padding: '12px',
              borderRadius: '5px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              {message}
            </div>
          )}

          {error && (
            <div className="error-message">{error}</div>
          )}

          {/* Profile Info */}
          <div className="server-card" style={{ marginBottom: '20px' }}>
            <h2 style={{ marginBottom: '15px', fontSize: '20px' }}>Account Information</h2>
            <div style={{ marginBottom: '15px' }}>
              <strong>Username:</strong> {user.username}
              {user.isAdmin && (
                <span className="admin-badge" style={{ marginLeft: '10px' }}>ADMIN</span>
              )}
            </div>
            <div style={{ marginBottom: '15px' }}>
              <strong>User ID:</strong> {user.id}
            </div>
            <div style={{ color: '#666', fontSize: '14px' }}>
              Note: Username cannot be changed
            </div>
          </div>

          {/* Update Email */}
          <div className="server-card" style={{ marginBottom: '20px' }}>
            <h2 style={{ marginBottom: '15px', fontSize: '20px' }}>Update Email</h2>
            <form onSubmit={handleEmailUpdate}>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
              </div>
              <button 
                type="submit" 
                className="btn" 
                disabled={loading}
                style={{ background: '#28a745' }}
              >
                {loading ? 'Updating...' : 'Update Email'}
              </button>
            </form>
          </div>

          {/* Change Password */}
          <div className="server-card">
            <h2 style={{ marginBottom: '15px', fontSize: '20px' }}>Change Password</h2>
            <form onSubmit={handlePasswordUpdate}>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <small style={{ color: '#666', fontSize: '13px' }}>
                  At least 6 characters
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <button 
                type="submit" 
                className="btn" 
                disabled={loading}
                style={{ background: '#ffc107', color: '#333' }}
              >
                {loading ? 'Updating...' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
